import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { formatET } from "../lib/dateUtils";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { StageTimeline } from "../components/shared/StageTimeline";
import { WhatDoesThisMean } from "../components/shared/WhatDoesThisMean";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { getClientNavItems } from "../modules/clientNav";
import { api, formatApiErrorDetail } from "../lib/api";
import { SUPPORT_EMAIL } from "../constants/brand";

const STAGES = ["Applied", "Under Review", "Approved", "Funded"];

const STAGE_EXPLAINERS = {
  Applied: "We've received your funding application and it's in the queue for review.",
  "Under Review": "Our lending partners are analyzing your profile across our 500+ lender network. You'll be notified within 2-3 business days.",
  Approved: "Great news — you've been approved for funding! We're finalizing the details before funds are released.",
  Funded: "Your funds have been released. Congratulations!",
  Declined: "This funding path wasn't approved this time. Our team can help you explore other options — reach out any time.",
};

export default function ClientFundingPage() {
  const { user } = useAuth();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("");

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/funding/status");
      setRecord(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const apply = async (e) => {
    e.preventDefault();
    try {
      await api.post("/funding/apply", { amount_requested: parseFloat(amount) });
      toast.success("Your funding application has been submitted");
      fetchStatus();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    }
  };

  const navItems = getClientNavItems(user?.unlocked_modules);
  const isDeclined = record?.stage === "Declined";
  const timelineStages = isDeclined ? [...STAGES.slice(0, 2), "Declined"] : STAGES;

  return (
    <DashboardLayout navItems={navItems} moduleName="Business Funding" moduleBackPath="/client">
      <div data-testid="client-funding-page" className="max-w-2xl">
        <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-2" title="Apply for business funding through IGC's lender network and track your application status.">Business Funding</h1>
        <p className="text-igc-muted text-sm mb-10">Explore funding options through IGC's proprietary capital placement pipeline.</p>

        {loading ? null : !record ? (
          <form onSubmit={apply} className="bg-igc-surface border border-igc-gold/15 p-10" data-testid="funding-apply-form">
            <p className="text-igc-cream mb-4">Ready to explore funding? Tell us how much you're looking for.</p>
            <label className="text-igc-muted text-xs uppercase tracking-widest mb-2 block">Estimated Amount Needed ($)</label>
            <Input data-testid="funding-amount-input" type="number" min="1" required value={amount} onChange={(e) => setAmount(e.target.value)} title="How much funding you're looking to raise for your business." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream h-12 mb-6" />
            <Button data-testid="funding-apply-button" type="submit" title="Submits your funding application to IGC's lender network." className="bg-igc-gold hover:bg-igc-goldHover text-black px-8">
              Apply Now
            </Button>
          </form>
        ) : (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-igc-surface border border-igc-gold/15 p-10" data-testid="funding-status-card">
            <StageTimeline stages={timelineStages} currentStage={record.stage} testId="funding-stage-timeline" />
            <p className={`text-center mt-10 font-serif text-xl ${isDeclined ? "text-igc-danger" : "text-igc-goldText"}`} data-testid="funding-current-stage">
              {record.stage}
              <WhatDoesThisMean testId="funding-stage-explainer" text={STAGE_EXPLAINERS[record.stage] || "Your funding application is being handled by our team."} />
            </p>
            <p className="text-igc-muted text-sm text-center mt-2">Requested: ${record.amount_requested.toLocaleString()}</p>
            {["Approved", "Funded"].includes(record.stage) && record.amount_funded != null && (
              <p className="text-igc-success text-center font-serif text-lg mt-4" data-testid="funding-funded-amount">
                {record.stage === "Funded" ? "Funded" : "Approved"}: ${record.amount_funded.toLocaleString()}
              </p>
            )}
            {record.updated_at && (
              <p className="text-igc-muted text-xs text-center mt-4" data-testid="funding-last-updated">
                Last updated {formatET(record.updated_at, "MMM d, yyyy 'at' h:mm a")}
              </p>
            )}
          </motion.div>
        )}

        <p className="text-igc-muted text-xs text-center mt-10">Questions? Contact us at {SUPPORT_EMAIL}</p>
      </div>
    </DashboardLayout>
  );
}
